import { FormEvent, useState } from "react";
import { Modal } from "../Modal";
import { Field, FormSection } from "../FormLayout";
import { apiFieldErrors } from "../../utils/apiError";
import type { Training } from "../../services/staffService";
import { TRAINING_TYPES } from "../../pages/trainingFilters";
import { leaveDays } from "../../pages/leaveFilters";

const FORM_ID = "schedule-training-form";
const FIELDS = [
  "name", "training_type", "start_date", "end_date", "duration_hours",
  "venue", "trainer", "description", "objectives",
] as const;

export default function ScheduleTrainingModal({
  isSaving,
  error,
  onSubmit,
  onClose,
}: {
  isSaving: boolean;
  error: unknown;
  onSubmit: (values: Partial<Training>) => void;
  onClose: () => void;
}) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [mandatory, setMandatory] = useState(false);
  const { fields: errors, message } = apiFieldErrors(
    error,
    FIELDS,
    "The training could not be scheduled. Please try again.",
  );
  const days = leaveDays(start, end);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (name: string) => String(form.get(name) ?? "").trim();
    onSubmit({
      name: text("name"),
      training_type: text("training_type"),
      start_date: start,
      end_date: end,
      duration_hours: Number(form.get("duration_hours")),
      venue: text("venue"),
      trainer: text("trainer"),
      description: text("description"),
      objectives: text("objectives"),
      is_mandatory: mandatory,
      is_active: true,
    });
  };

  return (
    <Modal
      isOpen
      size="2xl"
      title="Schedule training"
      onClose={() => {
        if (!isSaving) onClose();
      }}
      footer={
        <>
          <button type="button" className="btn btn-outline" disabled={isSaving} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={FORM_ID} disabled={isSaving} className="btn btn-primary">
            {isSaving ? "Scheduling…" : "Schedule training"}
          </button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={submit} className="font-refined space-y-5">
        {message && (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            {message}
          </p>
        )}

        <FormSection title="Session">
          <Field label="Training name *" name="name" errors={errors} className="sm:col-span-2">
            <input
              required
              name="name"
              maxLength={200}
              className="input mt-1"
              placeholder="e.g. First aid refresher"
              autoFocus
            />
          </Field>
          <Field label="Type *" name="training_type" errors={errors}>
            <select required name="training_type" defaultValue="professional" className="input mt-1">
              {TRAINING_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <label className="flex items-start gap-3 self-end rounded-md border border-gray-200 px-3 py-2.5 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={mandatory}
              onChange={(event) => setMandatory(event.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            <span>
              <span className="font-medium">Mandatory for all staff</span>
              <span className="block text-xs text-gray-500">Tracks who has still to complete it.</span>
            </span>
          </label>
        </FormSection>

        <FormSection title="When and where">
          <Field label="Starts *" name="start_date" errors={errors}>
            <input
              required
              type="date"
              value={start}
              onChange={(event) => {
                setStart(event.target.value);
                if (!end || end < event.target.value) setEnd(event.target.value);
              }}
              className="input mt-1"
            />
          </Field>
          <Field
            label="Ends *"
            name="end_date"
            errors={errors}
            hint={days > 1 ? `${days} days` : undefined}
          >
            <input
              required
              type="date"
              value={end}
              min={start || undefined}
              onChange={(event) => setEnd(event.target.value)}
              className="input mt-1"
            />
          </Field>
          <Field label="Total hours *" name="duration_hours" errors={errors} hint="Contact hours, counted towards CPD.">
            <input required type="number" min="1" name="duration_hours" className="input mt-1 tabular-nums" />
          </Field>
          <Field label="Venue" name="venue" errors={errors}>
            <input name="venue" maxLength={200} className="input mt-1" placeholder="e.g. Staff room" />
          </Field>
          <Field label="Trainer" name="trainer" errors={errors} className="sm:col-span-2">
            <input
              name="trainer"
              maxLength={200}
              className="input mt-1"
              placeholder="Person or organisation delivering it"
            />
          </Field>
        </FormSection>

        <FormSection title="Content" description="Optional, but helps staff know what to expect.">
          <Field label="Description" name="description" errors={errors} className="sm:col-span-2">
            <textarea name="description" rows={2} className="input mt-1 h-auto" />
          </Field>
          <Field label="Objectives" name="objectives" errors={errors} className="sm:col-span-2">
            <textarea
              name="objectives"
              rows={2}
              className="input mt-1 h-auto"
              placeholder="What staff should be able to do afterwards"
            />
          </Field>
        </FormSection>
      </form>
    </Modal>
  );
}
